/*
 * THE LIVE THREAD — what an agent's transcript looks like while the agent is still writing it.
 *
 * Two sources describe one run: the archive (durable, lands a row at a time) and the partial this
 * tab is driving (status, overwritten as it grows). Both are right; they overlap for the length of
 * every dispatch. This module holds the one rule that turns them into a single sequence, and it is
 * pure so the rule can be pinned by a test without a component.
 */
import { syncAggregates } from './conversation';
/*
 * THE ARCHIVE IS THE RECORD; THE OVERLAY IS ONLY WHAT IT DOES NOT HOLD YET.
 *
 * A step lands in the archive in pieces — the prose row, then the phase-1 tool row, then each
 * result — while the partial above keeps everything the step has written until the step ENDS. So
 * for the length of a tool's dispatch both existed at once, and a blocking tool (a question put to
 * the person, which waits for as long as they take) drew twice for as long as they took: the live
 * card AND the archived one, one above the other. Clearing the partial earlier only moves the seam:
 * the tab that drives the run clears it the instant its write is acknowledged, and the viewer reads
 * the archive a beat later, so the row vanishes and comes back.
 *
 * So neither side is asked to be fast. The viewer subtracts: every live part up to and including the
 * last one the archive already holds is dropped, and what remains is drawn AFTER the archived
 * sequence, inside the same turn. A tool is held when its id is archived; a sentence or a thought
 * when its text is. Once the step lands nothing remains, and the clear that follows changes
 * nothing on screen.
 */
export function unarchived(parts, archived) {
    const ids = new Set();
    const said = new Set();
    for (const p of archived) {
        if (p.kind === 'tool')
            ids.add(p.id);
        else
            said.add(p.text.trim());
    }
    let from = 0;
    parts.forEach((p, i) => {
        if (p.kind === 'tool' ? ids.has(p.id) : said.has(p.text.trim()))
            from = i + 1;
    });
    return parts.slice(from);
}
/**
 * THE THREAD AS IT IS RIGHT NOW: the archived turns, with what the run is writing continuing the
 * last one.
 *
 * The overlay used to be its own turn under the archived ones, so every part crossed a turn
 * boundary when its step landed — a fresh element for the same row, the spinner on a running call
 * starting over, the thought closing and reopening. Drawn as the TAIL of the last assistant turn
 * instead, its parts sit at the positions the archived ones will take, and the positional renderer
 * keeps every element in place across the hand-over.
 *
 * And while the run is unfinished, a call the archive recorded without an outcome is a call IN
 * FLIGHT — the row is drawn running, a question drawn waiting — rather than the unknown it would be
 * in a thread nobody is driving any more. The run decides; the record cannot.
 *
 * Pure. Returns the same array when there is nothing to add, so a renderer keyed on it sees no change.
 */
export function liveThread(turns, live, inFlight) {
    const last = turns[turns.length - 1];
    const base = last && last.role === 'assistant' && !last.returned && !last.fromRunId ? last : undefined;
    const rest = unarchived(live.parts, base?.parts ?? []);
    const settled = base ? liveTools(base, inFlight) : undefined;
    if (!rest.length)
        return settled && settled !== base ? [...turns.slice(0, -1), settled] : turns;
    const running = live.tools.filter((c) => rest.some((p) => p.kind === 'tool' && p.id === c.id)).map((c) => ({ ...c, state: 'running' }));
    const tail = rest[rest.length - 1];
    const turn = {
        ...(settled ?? { role: 'assistant', text: '' }),
        parts: [...(settled?.parts ?? []), ...rest.map((p, i) => (p.kind === 'reasoning' ? { ...p, live: i === rest.length - 1 } : p))],
        tools: [...(settled?.tools ?? []), ...running],
        streaming: true,
        thinking: tail?.kind === 'reasoning',
    };
    syncAggregates(turn);
    return settled ? [...turns.slice(0, -1), turn] : [...turns, turn];
}
/** The turn with its unresolved calls drawn as running while the run is live — or the turn itself
 *  when nothing changes, so an untouched thread keeps its identity. */
function liveTools(turn, inFlight) {
    if (!inFlight || !turn.tools?.some((c) => c.state === 'unknown'))
        return turn;
    return { ...turn, tools: turn.tools.map((c) => (c.state === 'unknown' ? { ...c, state: 'running' } : c)) };
}
//# sourceMappingURL=live-thread.js.map